import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { ShoppingBag, User, SignOut, CaretDown } from "@phosphor-icons/react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { code, changeCurrency } = useCurrency();
  const location = useLocation();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="brand-link">
          <img
            src="/logo.png"
            alt="WorldFoods2U"
            className="w-12 h-12 md:w-14 md:h-14 object-contain drop-shadow-[0_0_18px_rgba(212,175,55,0.35)] transition-transform duration-300 group-hover:rotate-6"
          />
          <div className="flex flex-col leading-none">
            <span className="font-display text-2xl tracking-tight text-cream">WorldFoods2U</span>
            <span className="text-[9px] tracking-[0.3em] text-gold/80 uppercase mt-1">Origin → Market</span>
          </div>
        </Link>

        <div className="hidden md:flex items-center gap-10 text-sm">
          <Link to="/shop" className={`transition-colors ${location.pathname.startsWith("/shop") ? "text-saffron" : "text-cream/75 hover:text-cream"}`} data-testid="nav-shop">
            Marketplace
          </Link>
          {user?.role === "farmer" && (
            <Link to="/farmer" className="text-cream/75 hover:text-cream" data-testid="nav-farmer">Farmer</Link>
          )}
          {user?.role === "buyer" && (
            <Link to="/buyer" className="text-cream/75 hover:text-cream" data-testid="nav-buyer">Buyer</Link>
          )}
          {user?.role === "admin" && (
            <Link to="/admin" className="text-cream/75 hover:text-cream" data-testid="nav-admin">Admin</Link>
          )}
          <Link to="/about" className="text-cream/75 hover:text-cream" data-testid="nav-about">Story</Link>
          <Link to="/demo" className="text-saffron hover:text-saffron-deep flex items-center gap-1.5" data-testid="nav-demo">
            <span className="w-1.5 h-1.5 rounded-full bg-saffron animate-pulse" />
            Demo
          </Link>
        </div>

        <div className="flex items-center gap-3">
          {/* Currency switcher */}
          <div className="relative group hidden sm:block">
            <button className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-cream/80 hover:text-saffron transition-colors py-2 px-3 rounded-full border border-white/10" data-testid="currency-btn">
              {code} <CaretDown size={10} />
            </button>
            <div className="absolute right-0 top-full mt-2 glass-strong rounded-xl overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all min-w-[140px] z-50">
              {["USD", "INR", "EUR"].map((c) => (
                <button
                  key={c}
                  onClick={() => changeCurrency(c)}
                  className={`w-full text-left px-4 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-saffron/10 ${c === code ? "text-saffron" : "text-cream/80"}`}
                  data-testid={`currency-${c}`}
                >
                  {c === "USD" && "🇺🇸 USD Dollar"}
                  {c === "INR" && "🇮🇳 INR Rupee"}
                  {c === "EUR" && "🇪🇺 EUR Euro"}
                </button>
              ))}
            </div>
          </div>

          {user?.role === "buyer" && (
            <Link to="/cart" className="relative p-2 text-cream/80 hover:text-saffron transition-colors" data-testid="nav-cart">
              <ShoppingBag size={22} weight="duotone" />
            </Link>
          )}
          {user ? (
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 glass rounded-full pr-4 pl-1 py-1">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-forest flex items-center justify-center text-xs">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                )}
                <span className="text-xs text-cream/90">{user.name?.split(" ")[0]}</span>
                {user.role && <span className="text-[9px] uppercase tracking-[0.15em] text-gold">{user.role}</span>}
              </div>
              <button onClick={logout} className="btn-ghost p-2" data-testid="logout-btn" aria-label="Logout">
                <SignOut size={20} weight="duotone" />
              </button>
            </div>
          ) : (
            <Link to="/login" className="btn-primary text-sm" data-testid="nav-login">
              <User size={16} weight="bold" /> Sign In
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
}
